import React from "react";
import { useMap } from "react-leaflet";
import { useEffect, useRef, useState, useMemo, useDeferredValue } from "react";
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux';
import { setFilter } from "../../store/reducers/stateReducer";
import { Virtuoso } from 'react-virtuoso';
import {
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    IconButton,
    InputAdornment,
    Typography,
    ThemeProvider,
    createTheme,
    Divider,
    CircularProgress,
    ListItemButton,
    Autocomplete,
    Chip
} from '@mui/material';
import {
    Clear as ClearIcon,
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { ktpIcon, projectStatusOreolIcon } from "../layers/icons";
import { aimToObject } from "../controls/helpers";

const statusColors = {
    'construction': '#ff2b2b',
    'reconstruction': '#2196F3',
    'replacement': '#18c218'
}

// Компонент для SVG иконки трансформатора
const TransformerIcon = ({ accounting, layer, onSelect }) => {
    // 1. Создаем ключ, уникальный для комбинации accounting и layer
    const key = `${accounting ? 'accounting' : 'default'}-${layer}`;

    // 2. Если иконка есть в кэше, возвращаем ее
    if (transformerIconCache.has(key)) {
        return transformerIconCache.get(key);
    }

    // 3. Если иконки нет, создаем ее
    const icon = (
        <Box sx={{ position: 'relative', width: 40, height: 40 }}>
            <Box style={{ position: 'absolute', margin: '8px 0 0 8px' }}>
                {ktpIcon(accounting ? 'accounting' : 'default', 20, 20)}
            </Box>
            {['2', '3', '4', '7'].includes(layer) ? (
                <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                    {projectStatusOreolIcon('ktp', Number(layer), 36)}
                </Box>
            ) : null}
        </Box>
    );

    // 4. Сохраняем новую иконку в кэш
    transformerIconCache.set(key, icon);

    // 5. Возвращаем созданную иконку
    return icon;
};

const transformerIconCache = new Map()

// Создаем темную тему
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        background: {
            default: '#1a1a1a',
            paper: '#2a2a2a',
        },
        text: {
            primary: '#ffffff',
            secondary: '#dddddd',
        },
        primary: {
            main: '#ffffff',
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1a1a1a',
                    borderColor: '#333',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#2a2a2a',
                        color: '#ffffff',
                        '& fieldset': {
                            borderColor: '#555',
                        },
                        '&:hover fieldset': {
                            borderColor: '#777',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#999',
                        },
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                    '&.Mui-checked': {
                        color: '#ffffff',
                    },
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    backgroundColor: '#3a3a3a',
                    marginBottom: '2px',
                    borderRadius: '4px',
                },
            },
        },
    },
});

const ModalTpFilter = (props) => {
    const map = useMap();
    const dispatch = useDispatch()
    const [text, setText] = useState('')
    const deferredText = useDeferredValue(text);
    const isSearching = text !== deferredText;
    const filter = useSelector(state => state.state.filter.tp)
    const projectsFilter = useSelector(state => state.state.filter.projects)
    const [localFilter, setLocalFilter] = useState(filter || [])
    const [localProjectsFilter, setLocalProjectsFilter] = useState(projectsFilter || [])
    const [expandedItems, setExpandedItems] = useState(new Set())

    const { buttonPosition } = props || {};
    const textRef = useRef()

    const electricity = useSelector(state => state.state.electricity)
    const projects = useSelector(state => state.state.projects)

    const transformers = useMemo(() => {
        return electricity.transformers.map((t) => {
            if (projectsFilter) {
                if ((t.projects || [0]).some(project => !projectsFilter.includes(project))) return null
            }
            const fs = electricity.feedingSubstations.find(fs => fs.fid === t.ps_id)
            return {
                ...t,
                name: `${fs ? fs.name + ' ' + fs.operating_voltage + ' ' : ''}${`${t.name}${t.nickname ? '(' + t.nickname + ')' : ''}`}${t.town ? ' ' + t.town : ''}`
            }
        }).filter(f => f)
    }, [electricity, projectsFilter])



    // Создаем плоский список для виртуализации
    const virtualizedData = useMemo(() => {
        if (!transformers) return [];

        // Группируем по стадиям
        const stageGroups = new Map();
        const rootTransformers = [];

        transformers.forEach(transformer => {
            if (transformer.stage && Object.keys(transformer.stage).length > 0) {
                // Есть стадия - группируем
                const stageKey = transformer.stage[Object.keys(transformer.stage)[0]]
                if (!stageKey) {
                    rootTransformers.push(transformer);
                    return
                }
                const stageName = `Этап ${stageKey || '-'}`;

                if (!stageGroups.has(stageName)) {
                    stageGroups.set(stageName, {
                        id: `stage_${stageKey}`,
                        name: stageName,
                        type: 'stage',
                        children: []
                    });
                }
                stageGroups.get(stageName).children.push(transformer);
            } else {
                // Нет стадии - помещаем в корень
                rootTransformers.push(transformer);
            }
        });

        // Сортируем стадии и трансформаторы
        const sortedStages = Array.from(stageGroups.values()).sort((a, b) => {
            const aMatch = a.name.match(/\d+/);
            const bMatch = b.name.match(/\d+/);
            const aNum = aMatch ? parseInt(aMatch[0]) : 0;
            const bNum = bMatch ? parseInt(bMatch[0]) : 0;
            return aNum - bNum;
        });

        // Сортируем трансформаторы в каждой стадии
        sortedStages.forEach(stage => {
            stage.children.sort((a, b) => a.name.localeCompare(b.name));
        });

        // Сортируем корневые трансформаторы
        rootTransformers.sort((a, b) => a.name.localeCompare(b.name));

        // Создаем плоский список для виртуализации
        const flatList = [];

        // Добавляем стадии и их трансформаторы
        sortedStages.forEach(stage => {
            flatList.push({
                id: stage.id,
                type: 'stage',
                name: stage.name,
                children: stage.children,
                level: 0
            });

            // Добавляем трансформаторы стадии только если стадия развернута
            // (это будет обрабатываться в ItemRenderer)
        });

        // Добавляем корневые трансформаторы
        rootTransformers.forEach(transformer => {
            flatList.push({
                ...transformer,
                type: 'transformers',
                level: 0
            });
        });

        return flatList;
    }, [transformers]);

    // Фильтруем данные для поиска
    const filteredVirtualizedData = useMemo(() => {
        if (!deferredText) return virtualizedData;

        const searchWords = deferredText.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);

        const filtered = [];
        let currentStage = null;

        virtualizedData.forEach(item => {
            if (item.type === 'stage') {
                // Проверяем, есть ли подходящие трансформаторы в стадии
                const matchingTransformers = item.children.filter(child =>
                    searchWords.every(word => child.name.toLowerCase().includes(word))
                );

                if (matchingTransformers.length > 0) {
                    currentStage = {
                        ...item,
                        children: matchingTransformers
                    };
                    filtered.push(currentStage);

                    // НЕ добавляем трансформаторы здесь - они будут добавлены в expandedVirtualizedData
                }
            } else if (item.type === 'transformers' && item.level === 0) {
                // Корневой трансформатор
                if (searchWords.every(word => item.name.toLowerCase().includes(word))) {
                    filtered.push(item);
                }
            }
        });

        return filtered;
    }, [virtualizedData, deferredText]);

    // Создаем расширенный список для виртуализации с учетом развернутых стадий
    const expandedVirtualizedData = useMemo(() => {
        const expanded = [];
        const stages = [];
        const rootTransformers = [];

        // Разделяем стадии и корневые трансформаторы
        filteredVirtualizedData.forEach(item => {
            if (item.type === 'stage') {
                stages.push(item);
            } else {
                rootTransformers.push(item);
            }
        });

        // Сортируем стадии: сначала те, где есть отмеченные трансформаторы
        stages.sort((a, b) => {
            const aCheckedCount = a.children.filter(t =>
                localFilter.length === 0 ? true : !localFilter.find(f => f === t.fid)
            ).length;
            const bCheckedCount = b.children.filter(t =>
                localFilter.length === 0 ? true : !localFilter.find(f => f === t.fid)
            ).length;

            // Если у стадии есть отмеченные трансформаторы, она идет выше
            if (aCheckedCount > 0 && bCheckedCount === 0) return -1;
            if (aCheckedCount === 0 && bCheckedCount > 0) return 1;

            // Если обе стадии имеют или не имеют отмеченных трансформаторов, сортируем по номеру этапа
            const aMatch = a.name.match(/\d+/);
            const bMatch = b.name.match(/\d+/);
            const aNum = aMatch ? parseInt(aMatch[0]) : 0;
            const bNum = bMatch ? parseInt(bMatch[0]) : 0;
            return aNum - bNum;
        });

        // Сортируем корневые трансформаторы: отмеченные вверху
        rootTransformers.sort((a, b) => {
            const aChecked = localFilter.length === 0 ? true : !localFilter.find(f => f === a.fid);
            const bChecked = localFilter.length === 0 ? true : !localFilter.find(f => f === b.fid);

            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;

            // Если оба имеют одинаковый статус, сортируем по имени
            return a.name.localeCompare(b.name);
        });

        // Добавляем стадии и их трансформаторы
        stages.forEach(stage => {
            expanded.push(stage);

            // Если стадия развернута ИЛИ идет поиск, добавляем ее трансформаторы
            if (expandedItems.has(stage.id) || deferredText) {
                // Сортируем трансформаторы в стадии: отмеченные вверху
                const sortedChildren = [...stage.children].sort((a, b) => {
                    const aChecked = localFilter.length === 0 ? true : !localFilter.find(f => f === a.fid);
                    const bChecked = localFilter.length === 0 ? true : !localFilter.find(f => f === b.fid);

                    if (aChecked && !bChecked) return -1;
                    if (!aChecked && bChecked) return 1;

                    // Если оба имеют одинаковый статус, сортируем по имени
                    return a.name.localeCompare(b.name);
                });

                sortedChildren.forEach(transformer => {
                    expanded.push({
                        ...transformer,
                        type: 'transformers',
                        level: 1,
                        parentStageId: stage.id
                    });
                });
            }
        });

        // Добавляем корневые трансформаторы
        rootTransformers.forEach(transformer => {
            expanded.push(transformer);
        });

        return expanded;
    }, [filteredVirtualizedData, expandedItems, localFilter, deferredText]);

    useEffect(() => {
        setTimeout(() => dispatch(setFilter({ 'tp': localFilter })), 100)
    }, [localFilter])

    useEffect(() => {
        setTimeout(() => dispatch(setFilter({ 'projects': localProjectsFilter })), 100)
    }, [localProjectsFilter])

    useEffect(() => {
        // Автоматически раскрываем все стадии при загрузке
        const stageIds = virtualizedData
            .filter(item => item.type === 'stage')
            .map(item => item.id);
        setExpandedItems(new Set(stageIds));
    }, [virtualizedData]);

    // Вычисляем стили позиционирования на основе позиции кнопки
    const dialogBoxStyles = useMemo(() => {
        const baseStyles = {
            position: 'absolute',
            zIndex: 1202,
            width: 'auto',
            minWidth: '280px',
            maxWidth: '350px',
            height: 'auto',
            maxHeight: '80vh',
            transformOrigin: 'left top',
            transform: window.innerWidth < 768 ? 'scale(0.8)' : 'scale(1)',
        };

        // Вычисляем позицию на основе позиции кнопки
        if (buttonPosition) {
            const modalWidth = 300;
            const modalHeight = 600;

            // Позиционируем справа от кнопки с отступом
            let left = buttonPosition.right + 8;
            let top = buttonPosition.top - 2 - (window.innerWidth < 768 ? 35 : 43);

            return {
                ...baseStyles,
                left: `${left}px`,
                top: `${top}px`,
                margin: 0
            };
        }

        // Дефолтное позиционирование (если buttonPosition не передан)
        return {
            ...baseStyles,
            margin: '10px 10px 5px 5px'
        };
    }, [buttonPosition]);

    const handleClearSearch = () => {
        setText('');
        if (textRef.current) {
            textRef.current.value = '';
        }
    };

    const handleSelectAllChange = (checked) => {
        if (checked) {
            setLocalFilter(prev => {
                return prev.filter(fid => !transformers.find(tr => tr.fid === fid))
            })
        } else {
            setLocalFilter(prev => ([...prev, ...transformers.map(tr => tr.fid).filter(fid => !prev.find(f => f === fid))]))
        }
    };

    const handleTransformerChange = (transformer, checked) => {
        if (checked) {
            // Если чекбокс отмечен, убираем трансформатор из фильтра (показываем его)
            setLocalFilter(prev => prev.filter(fid => fid !== transformer.fid));
        } else {
            // Если чекбокс не отмечен, добавляем трансформатор в фильтр (скрываем его)
            setLocalFilter(prev => {
                if (!prev.includes(transformer.fid)) {
                    return [...prev, transformer.fid];
                }
                return prev;
            });
        }
    };

    const handleToggleExpanded = (itemId) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    const isAllSelected = localFilter.length === 0 || localFilter.filter(fid => transformers.find(tr => tr.fid === fid)).length === 0;

    // Вычисляем статистику
    const statistics = useMemo(() => {
        // Общее количество этапов
        const totalStages = virtualizedData.filter(item => item.type === 'stage').length;

        // Выбранные этапы (этап считается выбранным, если хотя бы один его трансформатор выбран)
        const selectedStagesCount = virtualizedData
            .filter(item => item.type === 'stage')
            .filter(stage => {
                const hasSelectedTransformer = stage.children.some(child =>
                    localFilter.length === 0 ? true : !localFilter.find(f => f === child.fid)
                );
                return hasSelectedTransformer;
            }).length;

        // Общее количество трансформаторов
        const totalTransformers = transformers.length;

        // Выбранные трансформаторы (не в фильтре)
        const selectedTransformersCount = transformers.filter(t =>
            localFilter.length === 0 ? true : !localFilter.find(f => f === t.fid)
        ).length;

        return {
            stages: { selected: selectedStagesCount, total: totalStages },
            transformers: { selected: selectedTransformersCount, total: totalTransformers }
        };
    }, [virtualizedData, transformers, localFilter]);

    const handleProjectsChange = (newValue) => {
        setLocalProjectsFilter(newValue);
    };

    // Компонент для рендеринга элемента в виртуализированном списке
    const ItemRenderer = React.memo(({ item, index }) => {
        if (item.type === 'stage') {
            const stageTransformers = item.children || [];
            const checkedCount = stageTransformers.filter(t =>
                localFilter.length === 0 ? true : !localFilter.find(f => f === t.fid)
            ).length;
            const totalCount = stageTransformers.length;
            const isIndeterminate = checkedCount > 0 && checkedCount < totalCount;
            const isChecked = checkedCount === totalCount;
            const isExpanded = expandedItems.has(item.id);

            return (
                <ListItemButton
                    onClick={() => handleToggleExpanded(item.id)}
                    sx={{
                        pl: item.level * 0.5 + 0.5,
                        pr: 0.5,
                        py: 0.5,
                        backgroundColor: isExpanded ? '#484F46' : '#2a2a2a',
                        '&:hover': { backgroundColor: '#586156' },
                        borderBottom: '1px solid #444',
                        height: 40,
                        minHeight: 40
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 0.5 }}>
                        {stageTransformers.length > 0 ? (
                            isExpanded ? <ExpandLessIcon sx={{ color: '#7FA863', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                        ) : null}
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 0.5 }}>
                        <Checkbox
                            checked={isChecked}
                            size="small"
                            indeterminate={isIndeterminate}
                            onChange={(e) => {
                                e.stopPropagation(); // Останавливаем всплытие события
                                const checked = e.target.checked;
                                stageTransformers.forEach(transformer => {
                                    handleTransformerChange(transformer, checked);
                                });
                            }}
                            onClick={(e) => e.stopPropagation()} // Дополнительная защита от всплытия
                            sx={{ color: '#ffffff', padding: '2px' }}
                        />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Typography variant="body2" sx={{ color: isExpanded ? '#7FA863' : '#ffffff', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                {item.name} ({totalCount})
                            </Typography>
                        }
                        sx={{ margin: 0 }}
                    />
                </ListItemButton>
            );
        } else {
            // Это трансформатор
            const isChecked = localFilter.length === 0 ? true : !localFilter.find(f => f === item.fid);

            return (
                <ListItem
                    sx={{
                        pl: item.level * 1 + 1,
                        pr: 0.5,
                        py: 0,
                        backgroundColor: item.level === 1 ? '#484F46' : '#3a3a3a',
                        mb: 0,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: item.level === 1 ? '#586156' : '#4a4a4a' },
                        // height: 36,
                        // minHeight: 36
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 0.5 }}>
                        <Checkbox
                            checked={isChecked}
                            size="small"
                            onChange={(e) => handleTransformerChange(item, e.target.checked)}
                            sx={{ color: '#ffffff', padding: '2px' }}
                        />
                    </ListItemIcon>
                    <ListItemIcon
                        onClick={() => aimToObject(map, item)}
                        sx={{ minWidth: 'auto', mr: 0.5, cursor: 'pointer' }}
                    >
                        <TransformerIcon
                            accounting={item.accounting}
                            layer={item.layer}
                        />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Typography
                                onClick={() => aimToObject(map, item)}
                                variant="body2"
                                sx={{
                                    color: item.accounting ? '#F57795' : '#dddddd',
                                    fontSize: '0.8rem',
                                    lineHeight: '1.1',
                                    cursor: 'pointer'
                                }}
                            >
                                {item.name}
                            </Typography>
                        }
                        sx={{ margin: 0 }}
                    />
                </ListItem>
            );
        }
    });


    return (
        createPortal(
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ ...dialogBoxStyles, display: 'flex', flexDirection: 'column', gap: 1, width: 340 }}>
                    {/* Paper для Autocomplete проектов */}
                    <Paper
                        elevation={8}
                        sx={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: 1,
                            opacity: 0.95,
                            overflow: 'hidden',
                        }}
                    >
                        <Box>
                            <Autocomplete
                                multiple
                                size="small"
                                disableCloseOnSelect
                                options={projects || []}
                                getOptionLabel={(option) => option.name}
                                value={(projects || []).filter(project => localProjectsFilter.includes(project.id))}
                                onChange={(event, newValue) => handleProjectsChange(newValue.map(value => value.id))}
                                sx={{
                                    '& .MuiAutocomplete-root': {
                                        py: 0,
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Выберите проекты"
                                        sx={{
                                            '& .MuiAutocomplete-root': {
                                                py: 0,
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#2a2a2a',
                                                py: 0,
                                                minHeight: { xs: '26px', md: '34px' },
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                            }
                                        }}
                                    />
                                )}
                                
                                slotProps={{
                                    chip: {
                                        sx: { backgroundColor: '#3a3a3a', color: '#ffffff' },
                                        size: "small"
                                    },
                                    
                                    listbox: {

                                        sx: {
                                            
                                            backgroundColor: '#2a2a2a',
                                            '& .MuiAutocomplete-option': {
                                                color: '#ffffff',
                                                minHeight: '26px',
                                                px: 1,
                                                m: 0,
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                                '&:hover': {
                                                    backgroundColor: '#3a3a3a',
                                                },
                                                '&[aria-selected="true"]': {
                                                    backgroundColor: '#484F46',
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Paper>

                    {/* Paper для фильтра трансформаторов */}
                    <Paper
                        elevation={8}
                        sx={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: 1,
                            opacity: 0.95,
                            overflow: 'hidden',
                        }}
                    >
                        <Box>
                            {/* Поле поиска */}
                            <TextField
                                ref={textRef}
                                fullWidth
                                variant="outlined"
                                placeholder="Поиск..."
                                autoFocus
                                size="small"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#ffffff' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {text && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setText('');
                                                            textRef.current.value = '';
                                                        }}
                                                    >
                                                        {isSearching ? <CircularProgress size={20} /> : <ClearIcon />}
                                                    </IconButton>
                                                )}
                                            </InputAdornment>
                                        )
                                    }
                                }}
                                sx={{ mb: 0.5 }}
                            />

                            {/* Чекбокс "Вкл/выкл все" */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAllSelected}
                                        onChange={(e) => handleSelectAllChange(e.target.checked)}
                                        sx={{ color: '#ffffff', padding: '0px 10px' }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#ffffff' }}>
                                        Вкл/выкл все
                                    </Typography>
                                }
                                sx={{ mb: 0, ml: 0 }}
                            />

                            <Divider sx={{ mb: 0.5, borderColor: '#555' }} />

                            {/* Виртуализированный список */}
                            {expandedVirtualizedData && expandedVirtualizedData.length > 0 && (
                                <Box
                                    sx={{
                                        height: '400px',
                                        backgroundColor: '#3a3a3a',
                                        borderRadius: 1,
                                        p: 0.2,
                                    }}
                                >
                                    <Virtuoso
                                        totalCount={expandedVirtualizedData.length}
                                        itemContent={(index) => {
                                            const item = expandedVirtualizedData[index];
                                            return <ItemRenderer key={item.id || item.fid} item={item} index={index} />;
                                        }}
                                        style={{ height: '100%' }}
                                        overscan={5}
                                    />
                                </Box>
                            )}

                            {/* Сообщение если нет результатов и загрузка завершена */}
                            {expandedVirtualizedData.length === 0 && (
                                <Box
                                    sx={{
                                        height: '400px',
                                        backgroundColor: '#3a3a3a',
                                        borderRadius: 1,
                                        p: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: '100px'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#999', textAlign: 'center' }}
                                    >
                                        {text ? 'Ничего не найдено' : 'Нет данных для отображения'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Статистика */}
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    backgroundColor: '#2a2a2a',
                                    borderRadius: 1,
                                    mt: 0.5,
                                    display: 'flex',
                                    gap: 1.5,
                                    fontSize: '0.75rem'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#cccccc',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    Этапы: <strong>{statistics.stages.selected}/{statistics.stages.total}</strong>
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#cccccc',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    ТП: <strong>{statistics.transformers.selected}/{statistics.transformers.total}</strong>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </ThemeProvider>,
            document.body
        )
    );
};

export default ModalTpFilter;
